import _defineProperty from "@babel/runtime/helpers/defineProperty";
/* eslint-disable new-cap -- this is kind of enforced by Pino with the enum we have  */
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';
import { pino } from 'pino';
import pinoSerializers from 'pino-std-serializers';
import { ApplicationInsights, isServerApplicationInsightsClient } from './application-insights';
import { TRACK_THIS_PAGE_VIEW } from './constants';
import { formatError } from './internal/formatError';
import { isPlainObject } from './internal/isPlainObject';
import { loggerTestingEventEmitter } from './internal/loggerResetEventEmitter';
import applicationInsightsWebTransform from './internal/pino-transports/application-insights/applicationInsightsWebTransport';
import { consoleWebTransport } from './internal/pino-transports/console/consoleWebTransport';
import { printWarningWithTrace } from './internal/printWarningWithTrace';
import { SeverityLevels } from './types/SeverityLevels';
const isNextBuild = process.env.NEXT_PHASE === 'phase-production-build';
// @ts-expect-error -- this won't fail anything, but will be defined when on edge
const isInEdgeRuntime = typeof EdgeRuntime === 'string';
const loggerDebugIsEnabled = process.env.DEBUG_LOGGER === 'true';
const isTestingLogger = process.env.LOGGER_TEST === 'true';
const defaultParentSetupOptions = {
  pino: {
    useOnlyCustomLevels: true,
    customLevels: SeverityLevels,
    level: 'DEBUG',
    serializers: {
      // This is for environments that uses worker threads - currently only Node.js
      // Worker threads are known to not retain original prototypes and
      // only copies serializable values (anything that is enurable)
      error: pinoSerializers.errWithCause
    }
  },
  metadata: {
    isDevelopment: process.env.NODE_ENV !== 'production'
  }
};
const defaultChildSetupOptions = {
  pino: {},
  metadata: {}
};

// This is for production only and this means that
// only levels that are higher than this or equal level will be passed down into handlers
const MINIMUM_PRODUCTION_SEVERITY_LEVEL = 'METRIC';

/**
 * Logger for Oriflame applications.
 *
 * @example
 *
 * #### Simple
 *
 * This will only print info to console:
 *
 * ```ts
 * // Setup should occur before any logging is done, but can be called after to update metadata globally
 * Logger.configure({
 *    // ...setup properties
 * });
 * // Create instance with desired name or any other configuration...
 * const myLogger = new Logger({ name: 'application' });
 * // ...and do the logging 🎉
 * myLogger.info('This is just info'); // => [application]: This is just info
 * // You can even attach useful information in object as a second parameter
 * myLogger.info('This is info with other information', { weather: '🌤️' }); // => [application]: This is info with other information { metadata: { weather:  '🌤️' } }
 * ```
 *
 * #### With metadata
 *
 * You can add something whats called 'metadata'. Either globally during setup or locally for Logger instance.
 * These metadata properties are added to each log output (formatting may differr)
 *
 * ```ts
 * // Setup logger with global metadata
 * Logger.configure({
 *    metadata: {
 *      language: 'dk-DK',
 *      myProperty: {
 *        other: false
 *      }
 *    }
 * })
 * // Create your instance
 * const myLogger = new Logger('my-component', { weather: '🌤️' });
 * // And log it to see the results
 * myLogger.info('Hello there'); // => [my-component]: Hello there { language: 'dk-DK', myProperty: { other: false }, metadata: { weather: '🌤️' } }
 * ```
 */
export class Logger {
  constructor(name, loggerMetadataOrAreaName, loggerMetadata) {
    _defineProperty(this, "name", void 0);
    _defineProperty(this, "areaName", void 0);
    _defineProperty(this, "pinoChild", null);
    _defineProperty(this, "childSetupOptions", cloneDeep(defaultChildSetupOptions));
    this.name = name;
    this.areaName = typeof loggerMetadataOrAreaName === 'string' ? loggerMetadataOrAreaName : undefined;
    this.childSetupOptions.metadata = (typeof loggerMetadataOrAreaName === 'object' ? loggerMetadataOrAreaName : undefined) ?? loggerMetadata ?? defaultChildSetupOptions.metadata;
  }

  /**
   * Creates child Logger instance that describes more specific area
   * Is equivalent of `new Logger('<name>', <areaName>, ...)`
   */
  createChild(areaName, loggerMetadata = {}) {
    return new Logger(this.name, areaName, loggerMetadata);
  }

  /**
   * Overrides global metadata with new set of metadata
   */
  static setGlobalMetadata(metadata) {
    this.parentSetupOptions.metadata = deepmerge(defaultParentSetupOptions.metadata, metadata, {
      isMergeableObject: isPlainObject
    });
  }

  /**
   * Updates global metadata with provided metadata. Metadata that are provided will just be merged with already set metadata
   */
  static updateGlobalMetadata(metadata) {
    this.parentSetupOptions.metadata = deepmerge(this.parentSetupOptions.metadata, metadata, {
      isMergeableObject: isPlainObject
    });
  }
  static isInitialized() {
    return !!this.pinoParent;
  }

  /**
   * Tracks `pageView` manually.
   * Automatic `pageView` tracking in Application Insights is disabled to prevent `pageView` from being sent multiple times.
   * Can be called on the client and Application Insights must be already initialized.
   */
  static trackPageView() {
    const isCalledOnClient = typeof window !== 'undefined';
    if (isCalledOnClient && !this.isPageViewTracked && ApplicationInsights.isInitialized()) {
      ApplicationInsights.getForClient().trackPageView({
        properties: {
          TRACK_THIS_PAGE_VIEW
        }
      });
      this.isPageViewTracked = true;
    }
  }
  static initializeParent() {
    this.initializeTransports();

    // This disables debug calls altogether
    if (!this.parentSetupOptions.metadata.isDevelopment) {
      this.parentSetupOptions.pino.level = String(MINIMUM_PRODUCTION_SEVERITY_LEVEL);
    }
    this.pinoParent = pino(this.parentSetupOptions.pino);
    const {
      metadata
    } = this.parentSetupOptions;
    const shouldApplicationInsightsTransfersBeUsed = !metadata.isDevelopment && !isNextBuild;
    if (shouldApplicationInsightsTransfersBeUsed && !ApplicationInsights.isInitialized()) {
      let warningMessage = `Logger was initialized in production environment, but Application Insights were not initialized first.
      Make sure that ApplicationInsights.initialize(...) is called first`;
      if (isInEdgeRuntime) {
        warningMessage = `Logger is used in production environment edge runtime, but @ori/logger does not yet support edge runtime fully because of Application Insights.
Logging output will end up in console as a fallback`;
      }
      printWarningWithTrace(warningMessage);
    }
    if (loggerDebugIsEnabled) {
      printWarningWithTrace('Logger was initialized!');
    }

    // Logger reset for tests
    if (isTestingLogger) {
      loggerTestingEventEmitter.addListener('reset', () => {
        this.pinoParent = null;
        this.parentSetupOptions = cloneDeep(defaultParentSetupOptions);
      });
    }
  }
  static initializeTransports() {
    const {
      metadata
    } = this.parentSetupOptions;
    // In build we don't really count on application insights to be available
    // This is especially true when building in pipelines.
    // application insights is only useful during production runtime
    const isInNodeRuntime = typeof window === 'undefined' && !isInEdgeRuntime;
    const applicationInsightsIsInitialized = ApplicationInsights.isInitialized();
    const shouldUseApplicationInsights = !metadata.isDevelopment && !isNextBuild;
    if (isInNodeRuntime) {
      const pinoNodePipelines = [];
      if (shouldUseApplicationInsights && applicationInsightsIsInitialized) {
        pinoNodePipelines.push({
          target: require.resolve('./internal/pino-transports/application-insights/applicationInsightsNodeTransport'),
          options: global.oriAppInsightsSettings ?? {}
        });
      } else {
        // Pino pretty is just a fallback
        pinoNodePipelines.push({
          target: require.resolve('./internal/pino-transports/console/consoleNodeTransport')
        });
      }
      this.parentSetupOptions.pino.transport = {
        pipeline: pinoNodePipelines
      };
    }
    // Handle web or edge
    else {
      const pinoWebPipelineHandlers = [];
      if (applicationInsightsIsInitialized) {
        pinoWebPipelineHandlers.push(applicationInsightsWebTransform());
      }
      if (!applicationInsightsIsInitialized || !isInEdgeRuntime) {
        pinoWebPipelineHandlers.push(consoleWebTransport());
      }
      this.parentSetupOptions.pino.browser = {
        write(o) {
          for (const pipelineHandler of pinoWebPipelineHandlers) {
            pipelineHandler(o);
          }
        }
      };
    }
  }
  initializeChild() {
    const parent = Logger.getPinoParent();

    // Name should not part of message during production
    if (Logger.parentSetupOptions.metadata.isDevelopment) {
      const name = [this.name, this.areaName].filter(Boolean).join(' > ');
      this.childSetupOptions.pino.msgPrefix = `[${name}] `;
    }
    this.pinoChild = parent.child(
    // due to internal logic we have set this as empty object
    // as we create custom message with properties on each message
    // This is so that we can change properties
    {}, this.childSetupOptions.pino);

    // Logger reset for tests
    if (isTestingLogger) {
      loggerTestingEventEmitter.addListener('reset', () => {
        this.pinoChild = null;
        // This is so that reset works correctly, normally this.childSetupOptions.bindings won't be used anymore
        // after Logger was initialized
        this.childSetupOptions.metadata = cloneDeep(defaultChildSetupOptions.metadata);
      });
    }
  }
  static getPinoParent() {
    if (!this.isInitialized()) {
      this.initializeParent();
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- should be initialized by now
    return this.pinoParent;
  }
  getPinoChild() {
    if (!this.pinoChild) {
      this.initializeChild();
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- should be initialized by now
    return this.pinoChild;
  }
  createMessage(message, additionalMeta) {
    const metadata = deepmerge(this.childSetupOptions.metadata, additionalMeta ?? {}, {
      isMergeableObject: isPlainObject
    });
    // Ordering matters!
    let result = {
      ...message,
      ...Logger.parentSetupOptions.metadata,
      name: this.name,
      ...(this.areaName && {
        areaName: this.areaName
      }),
      ...(Object.keys(metadata).length && {
        metadata
      })
    };
    if (ApplicationInsights.isInitialized() && isServerApplicationInsightsClient(ApplicationInsights.get())) {
      const ai = ApplicationInsights.getForServer();
      // What a weird typing they have!
      const context = ai.getCorrelationContext();
      const {
        operation
      } = context ?? {};
      const {
        id,
        name,
        parentId
      } = operation ?? {};
      if (operation) {
        result = {
          ...result,
          applicationInsightsOperation: {
            id,
            name,
            parentId
          }
        };
      }
    }
    return result;
  }

  /**
   * Method for logging error
   *
   * @example
   *
   * #### Usage if you want to log error as is
   * ```js
   * const error = new Error('Some error message');
   * new Logger('@ori/awesome').error(error);
   * ```
   *
   * #### Usage if you want to give error additional description
   * ```js
   * const someOtherError = new Error('This is some specific error');
   * const error = new Error(
   *    // Here describe someOtherError in more detail
   *    'Some error message',
   *    {
   *      // You give previous error here
   *      cause: someOtherError,
   *    }
   * );
   * new Logger('@ori/awesome').error(error);
   * ```
   */
  error(error, metadata = {}) {
    this.getPinoChild().ERROR(this.createMessage({
      error: formatError(error)
    }, metadata));
  }

  /**
   * Method for logging metric
   *
   * @example Logging the speed of a function call
   * ```js
   *
   * ```
   */
  metric(name, value, metadata = {}) {
    this.getPinoChild().METRIC(this.createMessage({
      metricName: name,
      metricValue: value
    }, metadata));
  }

  /**
   * Logs info
   */
  info(message, metadata = {}) {
    this.getPinoChild().INFO(this.createMessage({}, metadata), message);
  }

  /**
   * Logs debug and is ommited in production mode
   */
  debug(message, metadata = {}) {
    this.getPinoChild().DEBUG(this.createMessage({}, metadata), message);
  }

  /**
   * Logs warning
   */
  warn(message, metadata = {}) {
    this.getPinoChild().WARN(this.createMessage({}, metadata), message);
  }

  /**
   * Sets new metadata object to this Logger instance.
   */
  setMetadata(metadata) {
    this.getPinoChild().setBindings(metadata);
    return this;
  }

  /**
   * Takes newly provided metadata and merges it with previously defined metadata on this Logger instance
   */
  updateMetadata(metadata) {
    const child = this.getPinoChild();
    this.setMetadata(deepmerge(child.bindings(), metadata, {
      isMergeableObject: isPlainObject
    }));
    return this;
  }
}
_defineProperty(Logger, "pinoParent", null);
_defineProperty(Logger, "parentSetupOptions", cloneDeep(defaultParentSetupOptions));
_defineProperty(Logger, "isPageViewTracked", false);
//# sourceMappingURL=Logger.js.map